import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../api/client";

export const BRANDING_UPDATED_EVENT = "leMemePrix:brandingUpdated";

const DEFAULT_SITE_NAME = "Le Même Prix";

export function normalizePickupLateGuarantee(raw) {
  if (!raw || typeof raw !== "object") {
    return { enabled: false, compensationEuro: 0, validityDays: 90 };
  }
  const enabled = raw.enabled === true || raw.enabled === "true" || raw.enabled === 1;
  let compensationEuro = Number(raw.compensationEuro);
  if (!Number.isFinite(compensationEuro) || compensationEuro < 0) compensationEuro = 0;
  if (enabled && compensationEuro <= 0) compensationEuro = 2;
  return {
    enabled,
    compensationEuro,
    validityDays: Number(raw.validityDays) > 0 ? Number(raw.validityDays) : 90,
  };
}

const SiteSettingsContext = createContext({
  siteName: DEFAULT_SITE_NAME,
  siteLogoUrl: "",
  pickupLateGuarantee: { enabled: false, compensationEuro: 0 },
  feedShareRewards: { enabled: false, pointsPerShare: 0, pointsPerEuro: 100 },
  loading: true,
  refreshSiteSettings: async () => {},
});

export function SiteSettingsProvider({ children }) {
  const [siteName, setSiteName] = useState(DEFAULT_SITE_NAME);
  const [siteLogoUrl, setSiteLogoUrl] = useState("");
  const [pickupLateGuarantee, setPickupLateGuarantee] = useState({ enabled: false, compensationEuro: 0 });
  const [feedShareRewards, setFeedShareRewards] = useState({
    enabled: false,
    pointsPerShare: 0,
    pointsPerEuro: 100,
  });
  const [loading, setLoading] = useState(true);

  const refreshSiteSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/api/site-settings");
      const settings = data?.settings || {};
      setSiteName(String(settings.siteName || DEFAULT_SITE_NAME).trim() || DEFAULT_SITE_NAME);
      setSiteLogoUrl(String(settings.siteLogoUrl || "").trim());
      setPickupLateGuarantee(normalizePickupLateGuarantee(settings.pickupLateGuarantee));
      setFeedShareRewards(normalizeFeedShareRewards(settings.feedShareRewards));
    } catch {
      setSiteName(DEFAULT_SITE_NAME);
      setSiteLogoUrl("");
      setPickupLateGuarantee(normalizePickupLateGuarantee(null));
      setFeedShareRewards(normalizeFeedShareRewards(null));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSiteSettings();
  }, [refreshSiteSettings]);

  useEffect(() => {
    const onBrandingUpdated = () => {
      refreshSiteSettings();
    };
    window.addEventListener(BRANDING_UPDATED_EVENT, onBrandingUpdated);
    return () => window.removeEventListener(BRANDING_UPDATED_EVENT, onBrandingUpdated);
  }, [refreshSiteSettings]);

  useEffect(() => {
    if (!siteLogoUrl) return;
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = siteLogoUrl;
  }, [siteLogoUrl]);

  const value = useMemo(
    () => ({
      siteName,
      siteLogoUrl,
      pickupLateGuarantee,
      feedShareRewards,
      loading,
      refreshSiteSettings,
    }),
    [siteName, siteLogoUrl, pickupLateGuarantee, feedShareRewards, loading, refreshSiteSettings]
  );

  return <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>;
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}

export function notifyBrandingUpdated() {
  window.dispatchEvent(new Event(BRANDING_UPDATED_EVENT));
}

export function normalizeFeedShareRewards(raw) {
  if (!raw || typeof raw !== "object") {
    return { enabled: false, pointsPerShare: 0, pointsPerEuro: 100 };
  }
  const enabled = raw.enabled === true || raw.enabled === "true" || raw.enabled === 1;
  const pointsPerShare = Math.max(0, Math.floor(Number(raw.pointsPerShare) || 0));
  const pointsPerEuro = Math.max(1, Math.floor(Number(raw.pointsPerEuro) || 100));
  return { enabled, pointsPerShare, pointsPerEuro };
}
